import { Autocomplete, TextField } from '@mui/material';
import { object } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Company from 'services/api/Company';
import { normalizeFeatureFlags } from 'utils/companyFeatureFlags';
import { I18n } from 'utils/i18n';

const FeatureFlagsField = ({ field, form }) => {
  const { name, onBlur } = field;
  const { errors, submitCount, setFieldValue, getFieldProps } = form;
  const { value } = getFieldProps(name);
  const selected = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Company.featureFlagCatalog()
      .then(flags => {
        if (!cancelled && Array.isArray(flags)) {
          setCatalog(flags);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(() => {
    const names = new Set(catalog);
    selected.forEach(flag => names.add(flag));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [catalog, selected]);

  return (
    <Autocomplete
      multiple
      freeSolo
      clearOnBlur
      disableCloseOnSelect
      filterSelectedOptions
      fullWidth
      options={options}
      value={selected}
      data-testid="company-feature-flags"
      onBlur={onBlur}
      onChange={(_, next) => {
        const names = next
          .map(item => (typeof item === 'string' ? item.trim() : ''))
          .filter(Boolean);
        setFieldValue(name, normalizeFeatureFlags(names));
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={
            selected.length === 0 ? I18n.t('CompanyForm.FeatureFlagsPlaceholder') : undefined
          }
          error={!!(errors[name] && submitCount > 0)}
          helperText={submitCount > 0 ? errors[name] : undefined}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps.htmlInput,
              'data-testid': 'company-feature-flags-input',
            },
          }}
        />
      )}
    />
  );
};

FeatureFlagsField.propTypes = {
  field: object.isRequired,
  form: object.isRequired,
};

export default FeatureFlagsField;
